import React, { FormEvent, useEffect, useId, useRef, useState } from 'react';
import Button from '../common/Button';
import Input from '../common/Input';
import { useAuth } from '../../hooks/useAuth';
import { authUtils } from '../../services/auth';
import '../Auth/RegisterModal.css';

interface PasswordLoginModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Full-page redirect after sign-in. Omit it to let the sign-in route guard redirect. */
  redirectTo?: string;
  onSuccess?: (response: any) => void;
  onForgotPassword: () => void;
}

interface FormErrors {
  username?: string;
  password?: string;
  general?: string;
}

const PasswordLoginModal: React.FC<PasswordLoginModalProps> = ({ isOpen, onClose, redirectTo, onSuccess, onForgotPassword }) => {
  const { login, isLoading: authLoading } = useAuth();
  const dialogRef = useRef<HTMLDivElement>(null);
  const usernameRef = useRef<HTMLInputElement>(null);
  const titleId = useId();
  const descriptionId = useId();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [errors, setErrors] = useState<FormErrors>({});
  const [localLoading, setLocalLoading] = useState(false);

  const isLoading = authLoading || localLoading;

  useEffect(() => {
    if (!isOpen) return;
    setUsername('');
    setPassword('');
    setErrors({});
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const previousFocus = document.activeElement as HTMLElement | null;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    usernameRef.current?.focus();
    const handleKeyDown = (event: KeyboardEvent) => {
      const dialog = dialogRef.current;
      if (!dialog) return;
      if (event.key === 'Escape' && dialog.getAttribute('aria-busy') !== 'true') {
        event.preventDefault();
        onClose();
      }
      if (event.key !== 'Tab') return;
      const elements = Array.from(dialog.querySelectorAll<HTMLElement>('button:not(:disabled), input:not(:disabled), [tabindex="0"]'));
      const first = elements[0];
      const last = elements[elements.length - 1];
      if (!first) {
        event.preventDefault();
        dialog.focus();
      } else if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = previousOverflow;
      previousFocus?.focus();
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!isLoading && e.target === e.currentTarget) onClose();
  };

  const validate = () => {
    const next: FormErrors = {};
    if (!username.trim()) next.username = 'Enter your username';
    if (!password.trim()) next.password = 'Enter your password';
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    setLocalLoading(true);
    try {
      await login({ username: username.trim(), password, rememberMe: false });
      if (onSuccess) {
        onSuccess({ user: { username } });
      }
      // Without an explicit target, PublicRoute/Login see the new session and go to the saved page or
      // /dashboard. Navigating here too would race them and could drop the saved page.
      if (redirectTo) {
        window.location.href = redirectTo;
      }
    } catch (err: any) {
      setErrors({ general: authUtils.formatAuthError(err) });
    } finally {
      setLocalLoading(false);
    }
  };

  return (
    <div className="register-modal-backdrop" onClick={handleBackdropClick}>
      <div ref={dialogRef} className="register-modal" role="dialog" aria-modal="true" aria-labelledby={titleId} aria-describedby={descriptionId} aria-busy={isLoading} tabIndex={-1}>
        <div className="register-modal__header">
          <h2 id={titleId} className="register-modal__title">Sign in with password</h2>
          <button
            type="button"
            className="register-modal__close"
            onClick={onClose}
            aria-label="Close password sign-in"
            disabled={isLoading}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        <form className="register-modal__form" onSubmit={handleSubmit} noValidate>
          <p id={descriptionId} className="login-form__route-description">Use your iKompete username and password.</p>
          {errors.general && (
            <div className="register-modal__error" role="alert">
              {errors.general}
            </div>
          )}

          <div className="register-modal__fields">
            <Input
              ref={usernameRef}
              type="text"
              name="username"
              label="Username"
              placeholder="Username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              error={errors.username}
              disabled={isLoading}
              autoComplete="username"
              autoCapitalize="none"
              spellCheck={false}
              required
            />

            <Input
              type="password"
              name="password"
              label="Password"
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              error={errors.password}
              disabled={isLoading}
              autoComplete="current-password"
              required
            />
          </div>

          <button type="button" className="login-form__forgot-password" onClick={onForgotPassword} disabled={isLoading}>
            Forgot your password?
          </button>

          <div className="register-modal__actions">
            <Button type="button" variant="secondary" onClick={onClose} disabled={isLoading}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" loading={isLoading} disabled={isLoading}>
              Sign In
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PasswordLoginModal;
